// src/utils/calculateTunnelFees.js

import { isHoliday } from '../data/hkHolidays';
import { tunnelTimeRangeFees } from '../data/tunnelTimeRangeFees';

function interpolateFee(rangeFee, totalSeconds, startTotalSeconds, endTotalSeconds) {
  const minutesPassed = Math.floor((totalSeconds - startTotalSeconds) / 60);
  const feeChangeInterval = Math.floor(minutesPassed / 2);
  const feeChangeIntervals = Math.floor((endTotalSeconds - startTotalSeconds) / 120);
  const feeChangeStep = Math.abs(rangeFee.end - rangeFee.start) / feeChangeIntervals;
  const feeChange = feeChangeInterval * feeChangeStep * (rangeFee.end > rangeFee.start ? 1 : -1);
  return rangeFee.start + feeChange;
}

function resolveVehicleFee(rangeFee, vehicle, totalSeconds, startTotalSeconds, endTotalSeconds) {
  const vehicleFee = rangeFee[vehicle];

  if (typeof vehicleFee === 'number') {
    return vehicleFee;
  }

  if (vehicleFee && typeof vehicleFee === 'object') {
    return interpolateFee(vehicleFee, totalSeconds, startTotalSeconds, endTotalSeconds);
  }

  return null;
}

function getHongKongTimeParts(dateTime) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Hong_Kong',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(dateTime);

  return {
    hours: Number(parts.find(part => part.type === 'hour').value),
    minutes: Number(parts.find(part => part.type === 'minute').value),
    seconds: Number(parts.find(part => part.type === 'second').value),
  };
}

function calculateFee(dateTime, tunnel, vehicle) {
  //console.log(dateTime);
  
  const { hours, minutes, seconds } = getHongKongTimeParts(dateTime);
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  
  const dayType = isHoliday(dateTime) ? 'holiday' : 'weekday';
  
  const tunnelData = tunnelTimeRangeFees.find(t => t.abbr === tunnel);
  if (!tunnelData) {
    console.log('No tunnel data found');
    return null;
  }

  const ranges = tunnelData[dayType];

  for (const range of ranges) {
    const [startHours, startMinutes, startSeconds = 0] = range.start.split(':').map(Number);
    const [endHours, endMinutes, endSeconds = 59] = range.end.split(':').map(Number);
    const startTotalSeconds = startHours * 3600 + startMinutes * 60 + startSeconds;
    const endTotalSeconds = endHours * 3600 + endMinutes * 60 + endSeconds;  
    if (totalSeconds >= startTotalSeconds && totalSeconds <= endTotalSeconds) {
      let fee;

      switch (tunnel) {
        case 'TCT':
        case 'TLT':
          fee = resolveVehicleFee(range.fee, vehicle, totalSeconds, startTotalSeconds, endTotalSeconds);
          break;

        case 'WHC':
        case 'CHT':
        case 'EHC':
          if (typeof range.fee === 'number') {
            fee = range.fee;
          } else if (dayType === 'holiday') {
            fee = range.fee;
          } else {
            fee = interpolateFee(range.fee, totalSeconds, startTotalSeconds, endTotalSeconds);
          }
          
          switch (vehicle) {
            case 'motorcycle':
              fee = Math.round(fee * 0.4 * 10) / 10;
              break;
            case 'car':
              // No change for cars
              break;
            // Add more vehicle types here as needed
            default:
              console.log('Unknown vehicle type');
              return null;
          }
          break;

        default:
          fee = range.fee;
          break;
      }

      if (typeof fee !== 'number' || Number.isNaN(fee)) {
        console.log('Invalid fee data');
        return null;
      }

      return Math.round(fee * 10) / 10;
    }
  }

  console.log('No matching time range found');
  return null;
}

export const calculateTunnelFees = (dateTime, vehicle) => {
  // Ensure dateTime is a Date object
  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
  
  return tunnelTimeRangeFees.map(tunnel => {
    const fee = calculateFee(date, tunnel.abbr, vehicle);
    return {
      abbr: tunnel.abbr,
      fee: fee
    };
  }).filter(tunnel => tunnel.fee !== null);
};
